"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AssertionError_1 = require("../error/AssertionError");
const EtudeError_1 = require("../error/EtudeError");
const EtudeParser_1 = require("../util/EtudeParser");
const Exceptional_1 = require("../util/Exceptional");
const MathUtil_1 = require("../util/MathUtil");
const Letter_1 = require("./Letter");
const MusicConstants_1 = require("./MusicConstants");
const Scale_1 = require("./Scale");
class Interval {
    constructor(quality, distance) {
        this.quality = quality;
        this.distance = distance;
        if (distance <= 0) {
            throw EtudeError_1.default.forInvalid(Interval, distance, "should be a positive integer");
        }
        switch (quality) {
            case Interval.Quality.PERFECT:
                if (!Interval.isPerfect(distance)) {
                    throw EtudeError_1.default.forInvalid(Interval, quality.toString() + distance, distance + " cannot have a perfect quality");
                }
                break;
            case Interval.Quality.MAJOR:
            case Interval.Quality.MINOR:
                if (Interval.isPerfect(distance)) {
                    throw EtudeError_1.default.forInvalid(Interval, quality.toString() + distance, distance + " cannot have major or minor quality");
                }
                break;
            case Interval.Quality.DIMINISHED:
            case Interval.Quality.DOUBLY_DIMINISHED:
            case Interval.Quality.AUGMENTED:
            case Interval.Quality.DOUBLY_AUGMENTED:
                break;
        }
    }
    static isEnharmonic(a, b) {
        return a.getOffset() === b.getOffset();
    }
    static between(a, b) {
        const letterA = a.getKey().getLetter();
        const letterB = b.getKey().getLetter();
        /**
         * 1 (because no distance == 1)
         * + letterDistance (subtracted 2 because C is the start of the octave)
         * + octaveDistance
         */
        const distance = 1
            + (MathUtil_1.default.floorMod(letterB.ordinal() - 2, Letter_1.default.size) - MathUtil_1.default.floorMod(letterA.ordinal() - 2, Letter_1.default.size))
            + (b.getOctave() - a.getOctave()) * Letter_1.default.size;
        if (distance <= 0) {
            return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Interval, distance, "cannot create an interval with a nonpositive number"));
        }
        let offset = (b.getProgramNumber() - a.getProgramNumber()) % MusicConstants_1.default.KEYS_IN_OCTAVE;
        offset -= Scale_1.default.Quality.MAJOR
            .getStepPattern()
            .slice(0, (distance - 1) % Letter_1.default.size)
            .reduce(MathUtil_1.default.add, 0);
        let quality;
        switch (offset) {
            case -3:
                if (Interval.isPerfect(distance)) {
                    return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Interval, distance, "cannot create a perfect interval with an offset of -3"));
                }
                quality = Interval.Quality.DOUBLY_DIMINISHED;
                break;
            case -2:
                quality = Interval.isPerfect(distance) ? Interval.Quality.DOUBLY_DIMINISHED : Interval.Quality.DIMINISHED;
                break;
            case -1:
                quality = Interval.isPerfect(distance) ? Interval.Quality.DIMINISHED : Interval.Quality.MINOR;
                break;
            case 0:
                quality = Interval.isPerfect(distance) ? Interval.Quality.PERFECT : Interval.Quality.MAJOR;
                break;
            case 1:
                quality = Interval.Quality.AUGMENTED;
                break;
            case 2:
                quality = Interval.Quality.DOUBLY_AUGMENTED;
                break;
            default:
                return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Interval, offset, "offset is too large"));
        }
        return Exceptional_1.default.of(new Interval(quality, distance));
    }
    isSimple() {
        return this.distance <= Letter_1.default.size + 1;
    }
    isCompound() {
        return this.distance > Letter_1.default.size + 1;
    }
    /**
     * follows the rules on https://en.wikipedia.org/wiki/Interval_(music)#Inversion
     * important for the inverse of compound intervals for which definitions online differ
     */
    inverse() {
        let invertedQuality;
        switch (this.quality) {
            case Interval.Quality.PERFECT:
                invertedQuality = Interval.Quality.PERFECT;
                break;
            case Interval.Quality.MAJOR:
                invertedQuality = Interval.Quality.MINOR;
                break;
            case Interval.Quality.MINOR:
                invertedQuality = Interval.Quality.MAJOR;
                break;
            case Interval.Quality.DIMINISHED:
                invertedQuality = Interval.Quality.AUGMENTED;
                break;
            case Interval.Quality.DOUBLY_DIMINISHED:
                invertedQuality = Interval.Quality.DOUBLY_AUGMENTED;
                break;
            case Interval.Quality.AUGMENTED:
                invertedQuality = Interval.Quality.DIMINISHED;
                break;
            case Interval.Quality.DOUBLY_AUGMENTED:
                invertedQuality = Interval.Quality.DOUBLY_DIMINISHED;
                break;
            default:
                throw new AssertionError_1.default("Interval switch case");
        }
        // inverses add up to 9 assuming number is in the range [1, 8]
        const invertedNumber = this.distance != 1 && this.distance % Letter_1.default.size == 1 ? 1 : 9 - (this.distance % Letter_1.default.size);
        return new Interval(invertedQuality, invertedNumber);
    }
    getOffset() {
        // initialize offset to take into account octave
        let offset = Math.trunc((this.distance - 1) / Letter_1.default.size) * MusicConstants_1.default.KEYS_IN_OCTAVE;
        // take into account normalized number (within the range of an octave)
        offset += Scale_1.default.Quality.MAJOR
            .getStepPattern()
            .slice(0, this.distance - 1)
            .reduce(MathUtil_1.default.add, 0);
        // take into account quality
        switch (this.quality) {
            case Interval.Quality.PERFECT:
            case Interval.Quality.MAJOR:
                break;
            case Interval.Quality.MINOR:
                --offset;
                break;
            case Interval.Quality.DIMINISHED:
                offset -= Interval.isPerfect(this.distance) ? 1 : 2;
                break;
            case Interval.Quality.DOUBLY_DIMINISHED:
                offset -= Interval.isPerfect(this.distance) ? 2 : 3;
                break;
            case Interval.Quality.AUGMENTED:
                ++offset;
                break;
            case Interval.Quality.DOUBLY_AUGMENTED:
                offset += 2;
                break;
        }
        return offset;
    }
    static isPerfect(distance) {
        let normalized = distance % 7;
        return normalized === 1 || normalized === 4 || normalized === 5;
    }
    static fromString(intervalString) {
        return EtudeParser_1.default
            .of(intervalString)
            .filter(o => o != null, EtudeError_1.default.forNull(Interval))
            .map(s => s.match(/\D+|\d+/g))
            .filter(s => s.length >= 2, EtudeError_1.default.forInvalid(Interval, intervalString, "missing information"))
            .filter(s => s.length <= 2, EtudeError_1.default.forInvalid(Interval, intervalString, "contains extra information"))
            .parse(s => Interval.Quality.fromString(s[0]))
            .parse(s => Exceptional_1.default
            .of(s[1])
            .map(i => i.match("\\d+"), EtudeError_1.default.forInvalid(Interval, intervalString, "doesn't contain a valid number"))
            .map(parseInt))
            .get(a => new Interval(a[0], a[1]));
    }
    toString() {
        return this.quality.toString() + this.distance;
    }
    getQuality() {
        return this.quality;
    }
    getDistance() {
        return this.distance;
    }
}
(function (Interval) {
    class Quality {
        constructor(symbol) {
            this.symbol = symbol;
            ++Quality.size;
            Quality._values.push(this);
        }
        static values() {
            return Quality._values.slice();
        }
        ordinal() {
            return Quality._values.indexOf(this);
        }
        static valueOf(qualityString) {
            const quality = Quality[qualityString];
            if (quality instanceof Quality) {
                return quality;
            }
            throw EtudeError_1.default.forInvalid(Quality, qualityString);
        }
        static fromString(qualityString) {
            return EtudeParser_1.default
                .of(qualityString)
                .filter(o => o != null, EtudeError_1.default.forNull(Quality))
                .parse(s => {
                const value = Quality
                    .values()
                    .find(a => s.localeCompare(a) === 0);
                return Exceptional_1.default
                    .ofNullable(value)
                    .withException(EtudeError_1.default.forInvalid(Quality, s));
            })
                .get(a => a[0]);
        }
        toString() {
            return this.symbol;
        }
    }
    Quality.size = 0;
    Quality._values = [];
    Quality.PERFECT = new Quality("P");
    Quality.MAJOR = new Quality("M");
    Quality.MINOR = new Quality("m");
    Quality.DIMINISHED = new Quality("d");
    Quality.DOUBLY_DIMINISHED = new Quality("dd");
    Quality.AUGMENTED = new Quality("A");
    Quality.DOUBLY_AUGMENTED = new Quality("AA");
    Interval.Quality = Quality;
})(Interval || (Interval = {}));
exports.default = Interval;
